
import type { DungeonState } from "../net/messages";
import { elem, styleInstaller } from "./chrome";

export interface DungeonActions {
  /** Press the door. No arguments -- see `net/connection.ts`. */
  onUseDoor?: () => void;
}

/** What the button should say, given a frame. Exported so the test can assert
 *  the mapping rather than scrape the DOM for it. */
export function buttonLabel(state: DungeonState): string {
  return state.inside ? "Leave" : "Enter";
}

export function describe(state: DungeonState): string {
  if (state.inside) {
    const kills = state.mob_kills + state.stone_kills;
    const heads = state.members === 1 ? "you are alone here" : `${state.members} inside`;
    return `${kills} killed, ${heads}.`;
  }
  if (state.door) {
    return state.min_level > 1
      ? `A way into ${state.name}. It asks for level ${state.min_level}.`
      : `A way into ${state.name}.`;
  }
  return "No way down from here.";
}

/** Whether there is anything to draw at all -- inside a dungeon, or standing at
 *  a door. The panel hides itself otherwise, so it costs nothing on the ninety
 *  nine per cent of the map that is neither. */
export function relevant(state: DungeonState): boolean {
  return state.inside || state.door !== 0;
}

const STYLE_ID = "dungeon-style";

const STYLE = `
.dungeon {
  position: fixed;
  right: 12px;
  /* Above the task bar, the way every window anchored to the foot of the
     screen is: characterwindow.py:19 subtracts taskbar.py's 37. A fixed top
     put the Enter button under the foot of an 800x600 screen. */
  bottom: 37px;
  z-index: 58;
  width: 196px;
  padding: 9px 11px 10px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.86);
  border: 1px solid rgba(200, 170, 120, 0.24);
  pointer-events: auto;
  font: 12px/1.45 system-ui, sans-serif;
  color: #d8dee6;
}
.dungeon[hidden] { display: none; }
.dungeon-title {
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c4a884;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}
.dungeon-where { color: #d4bb9f; }
.dungeon-line { color: #b8c6d8; font-size: 11px; min-height: 2.6em; }
.dungeon-button {
  width: 100%;
  margin-top: 6px;
  padding: 5px 0;
  border-radius: 6px;
  border: 1px solid rgba(200, 170, 120, 0.34);
  background: rgba(200, 170, 120, 0.1);
  color: #f0dcc0;
  font: inherit;
  cursor: pointer;
}
.dungeon-button:hover:not(:disabled) { background: rgba(200, 170, 120, 0.22); }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class DungeonPanel {
  private readonly root: HTMLElement;
  private readonly where: HTMLElement;
  private readonly line: HTMLElement;
  private readonly button: HTMLButtonElement;

  /** The frame exactly as last sent. What the button does is decided by this
   *  and never by a flag of this panel's own: a player can leave a dungeon
   *  without pressing anything -- a disconnection does it -- and the server is
   *  the only thing that knows. */
  private drawn: DungeonState | null = null;

  /** Set once the player has hidden the panel by hand, so that a frame arriving
   *  afterwards does not reopen it. Standing at a door is a *place*, and a
   *  panel that sprang back every second while a player stood talking to the
   *  guard would be a panel they could not close. */
  private dismissed = false;

  constructor(
    container: HTMLElement,
    private readonly actions: DungeonActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "dungeon");
    this.root.hidden = true;

    const title = elem(doc, "div", "dungeon-title");
    const heading = doc.createElement("span");
    heading.textContent = "Dungeon";
    this.where = elem(doc, "span", "dungeon-where");
    title.append(heading, this.where);

    this.line = elem(doc, "div", "dungeon-line");

    this.button = doc.createElement("button");
    this.button.type = "button";
    this.button.className = "dungeon-button";
    this.button.textContent = "Enter";
    this.button.addEventListener("click", () => this.press());

    this.root.append(title, this.line, this.button);
    container.append(this.root);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get state(): DungeonState | null {
    return this.drawn;
  }

  /** Whether the player is on a dungeon floor, as the server last said. */
  get inside(): boolean {
    return this.drawn !== null && this.drawn.inside;
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
    this.dismissed = !visible;
  }

  toggle(): void {
    this.setVisible(this.root.hidden);
  }

  /** Press the door. From the button or from the key handler, so that one
   *  gesture and one button cannot disagree about which they are. */
  press(): void {
    this.actions.onUseDoor?.();
  }

  update(state: DungeonState): void {
    const arrived = state.inside && !this.inside;
    this.drawn = state;

    // Arriving on a floor reopens the panel even if it was dismissed. Entering
    // a dungeon is not an incidental event a player might want out of the way;
    // it is the thing they just did, and the button that gets them out again
    // is on this panel.
    if (arrived) this.dismissed = false;

    this.where.textContent = state.inside ? state.name : "";
    this.line.textContent = describe(state);
    this.button.textContent = buttonLabel(state);

    if (!relevant(state)) {
      this.root.hidden = true;
      return;
    }
    this.root.hidden = this.dismissed;
  }
}
